import { Link } from 'react-router-dom';
import type { AiCreditsDto } from '@order-pilot/shared';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function renewsOn(now = new Date()): string {
  return `1 ${MONTHS[(now.getMonth() + 1) % 12]}`;
}

/** Below a fifth of what the tenant is living on, or nothing left at all. */
export function isLow(c: AiCreditsDto): boolean {
  if (!c.enabled) return false;
  if (c.available <= 0) return true;
  if (c.allowance > 0) return c.boost === 0 && c.allowance - c.usedThisMonth < c.allowance * 0.2;
  return c.boostGranted > 0 && c.boost < c.boostGranted * 0.2;
}

/** "84 of 100 left · 20 boost", the one-line balance. */
export function creditsLine(c: AiCreditsDto): string {
  if (!c.enabled) return c.plan ? `${c.plan} plan` : 'No plan';
  const monthly = c.allowance > 0 ? `${Math.max(c.allowance - c.usedThisMonth, 0)} of ${c.allowance} left` : '';
  const boost = c.boostGranted > 0 ? (c.allowance > 0 ? `${c.boost} boost` : `${c.boost} of ${c.boostGranted} boost left`) : '';
  return [monthly, boost].filter(Boolean).join(' · ') || '0 credits left';
}

function Meter({ label, left, total, low }: { label: string; left: number; total: number; low: boolean }) {
  const pct = total > 0 ? Math.round((left / total) * 100) : 0;
  return (
    <div className="mt-2.5">
      <div className="flex items-baseline justify-between text-xs">
        <span className="text-slate-600">{label}</span>
        <span className="tabular-nums text-slate-900">
          {left} <span className="text-slate-500">of {total} left</span>
        </span>
      </div>
      <div
        className="mt-1 h-1.5 overflow-hidden rounded-full bg-brand-600/15"
        role="progressbar"
        aria-label={`${label} credits left`}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={left}
      >
        <div className={`h-full rounded-full ${left <= 0 ? 'bg-slate-400' : low ? 'bg-amber-500' : 'bg-brand-600'}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/** Ask Pilot's credits card: the bars, the renewal date and the way into the history. */
export function CreditsCard({ credits }: { credits: AiCreditsDto }) {
  const low = isLow(credits);
  return (
    <section aria-label="AI credits" className="card p-4">
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-semibold text-slate-900">
          <span className="text-brand-600">✦</span> AI credits
        </span>
        {credits.allowance > 0 ? <span className="text-xs text-slate-500">Renews {renewsOn()}</span> : null}
      </div>
      {credits.enabled ? (
        <>
          {credits.allowance > 0 ? (
            <Meter label="This month" left={Math.max(credits.allowance - credits.usedThisMonth, 0)} total={credits.allowance} low={low} />
          ) : null}
          {credits.boostGranted > 0 ? <Meter label="Boost" left={credits.boost} total={credits.boostGranted} low={credits.allowance === 0 && low} /> : null}
        </>
      ) : (
        <p className="mt-2 text-xs text-slate-600">Included with the Growth, Business and Custom plans.</p>
      )}
      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="text-slate-500">1 credit a question · {credits.dailyLeft} left today</span>
        <Link to="/ask-pilot/usage" className="font-medium text-brand-700 hover:underline">
          View usage →
        </Link>
      </div>
    </section>
  );
}
